import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GroupsIcon from '@mui/icons-material/Groups';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RuleScreen from '../../shared/RuleScreen';
import SundargarhFilterBar from '../../shared/SundargarhFilterBar';
import StatTile from '../../shared/StatTile';
import DataTable from '../../shared/DataTable';
import Pagination, { normalizePagination } from '../../shared/Pagination';
import DetailDrawer from '../../shared/DetailDrawer';
import { buildParams } from '../../shared/apiParams';

const API_URL = '/api/survey'; // Express backend (services/sundargarhData.js), same live-Kobo data source as the old Vercel functions
const EMPTY = { ulbs: [], wards: [], enumerators: [], startDate: '', endDate: '' };

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'ulb', label: 'By ULB', col: 'ULB' },
  { key: 'ward', label: 'By Ward', col: 'Ward' },
];

const pctCell = (b, highlight) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: highlight ? 'var(--danger)' : 'var(--muted)', fontWeight: highlight ? 700 : 400, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

// Note: this only checks beneficiary > eligible — the internal rule also
// compares eligible against an auto-calculated count, but this Kobo form has
// no equivalent auto field to compare against.
export default function SundargarhSubhadra({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, filter: 'all', records: [], loading: false });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/sundargarh/rules/subhadra/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching Sundargarh Subhadra data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalSurveys = summary?.totalSurveys ?? rows.reduce((s, r) => s + (Number(r.totalSurveys) || 0), 0);

  const tiles = (
    <>
      <StatTile label="Total Surveys" value={totalSurveys} icon={GroupsIcon} accent="var(--primary)" />
      <StatTile label="Eligible Women (total)" value={summary?.eligibleWomenTotal ?? 0} icon={VolunteerActivismIcon} accent="var(--cat-subhadra)" />
      <StatTile label="Beneficiaries (total)" value={summary?.beneficiaryTotal ?? 0} icon={VolunteerActivismIcon} accent="var(--cat-subhadra)" />
      <StatTile
        label="Beneficiary > Eligible"
        value={`${summary?.percentages?.beneficiaryGreater ?? 0}%`}
        icon={WarningAmberIcon}
        accent={(summary?.percentages?.beneficiaryGreater ?? 0) >= 10 ? 'var(--danger)' : 'var(--warning)'}
        sub={`${summary?.flagCounts?.beneficiaryGreater ?? 0} of ${totalSurveys} surveys`}
      />
    </>
  );

  const openDrilldown = async (row, filter = 'all') => {
    setDrawer({ open: true, row, filter, records: [], loading: true });
    try {
      const params = buildParams(applied, { extra: { groupBy: activeTab, groupValue: row.name, filter, limit: 100 } });
      const res = await axios.get(`${API_URL}/sundargarh/rules/subhadra/details?${params}`);
      setDrawer({ open: true, row, filter, records: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching Subhadra detail records:', err);
      setDrawer({ open: true, row, filter, records: [], loading: false });
    }
  };

  const flagCell = (row) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, 'beneficiaryGreater'); }}>
      {pctCell(row.flagCounts ? { count: row.flagCounts.beneficiaryGreater, pct: row.percentages?.beneficiaryGreater } : null, true)}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys || 0}</span> },
    { key: 'eligibleWomenTotal', label: 'Eligible Women', align: 'center', render: (r) => r.eligibleWomenTotal || 0 },
    { key: 'beneficiaryTotal', label: 'Beneficiary', align: 'center', render: (r) => r.beneficiaryTotal || 0 },
    { key: 'beneficiaryGreater', label: 'Beneficiary > Eligible', align: 'center', render: (r) => flagCell(r) },
  ];

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    { key: 'household_id', label: 'Household ID', render: (r) => r.household_id || '—' },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'subhadraEligible', label: 'Eligible', align: 'center', render: (r) => r.subhadraEligible ?? '—' },
    { key: 'subhadraBeneficiary', label: 'Beneficiary', align: 'center', render: (r) => r.subhadraBeneficiary ?? '—' },
    { key: 'ulb', label: 'ULB', render: (r) => r.ulb || '—' },
    { key: 'ward', label: 'Ward', render: (r) => r.ward || '—' },
  ];

  return (
    <RuleScreen
      title={rule?.title || 'Subhadra Eligibility'}
      subtitle="Flags households where the reported Subhadra beneficiary count exceeds the reported eligible-women count."
      icon={VolunteerActivismIcon}
      backTo={rule?.category ? `/sundargarh-category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button key={t.key} className={`tab-btn${activeTab === t.key ? ' active' : ''}`} onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}>
                {t.label}
              </button>
            ))}
          </div>
          <SundargarhFilterBar onApply={(v) => { setApplied(v); setPage(1); }} onClear={() => { setApplied(EMPTY); setPage(1); }} loading={loading} />
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Subhadra Statistics by {tab.label.replace('By ', '')}</h3>
        <Pagination pagination={pagination} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} unitLabel="groups" shown={rows.length} />
        <DataTable columns={columns} rows={rows} loading={loading} emptyMessage='No data found. Adjust filters and click "Apply Filters".' onRowClick={(r) => openDrilldown(r, 'all')} getRowKey={(r, i) => `${r.name}-${i}`} />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name}` : ''}
        sections={
          drawer.row
            ? [{
                title: 'Totals',
                items: [
                  { label: 'Total Surveys', value: drawer.row.totalSurveys },
                  { label: 'Eligible Women', value: drawer.row.eligibleWomenTotal || 0 },
                  { label: 'Beneficiary', value: drawer.row.beneficiaryTotal || 0 },
                  { label: 'Beneficiary > Eligible', value: `${drawer.row.flagCounts?.beneficiaryGreater || 0} (${drawer.row.percentages?.beneficiaryGreater || 0}%)` },
                ],
              }]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <DataTable columns={drawerRecordColumns} rows={drawer.records} loading={drawer.loading} emptyMessage="No households found." getRowKey={(r, i) => r.uuid || i} />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
